/* Linktree JS - resilient interactions + smart routing */
(() => {
  'use strict';

  const page = document.querySelector('[data-linktree-page]');
  if (!page) return;

  const linksSection = page.querySelector('[data-linktree-links]');
  const utmOn = linksSection?.dataset.ltUtmOn === 'true';
  const utmStr = (linksSection?.dataset.ltUtm || '').trim();
  const LAST_LINK_KEY = 'lt_last_link';
  const marketingHrefBeforeMutation = new Map();

  function syncYouTubeThumbnails() {
    page.querySelectorAll('[data-lt-youtube-thumb]').forEach((image) => {
      if (window.flCanTrackMarketing === true) {
        if (!image.getAttribute('src')) image.setAttribute('src', image.dataset.src || '');
      } else {
        image.removeAttribute('src');
      }
    });
  }

  document.addEventListener('fl:privacy-consent-change', syncYouTubeThumbnails);
  window.flRunWhenPrivacyReady(syncYouTubeThumbnails);

  function isTrackableHref(href) {
    if (!href) return false;
    const lower = href.toLowerCase();
    return !lower.startsWith('#') && !lower.startsWith('mailto:') && !lower.startsWith('tel:') && !lower.startsWith('javascript:');
  }

  function appendUtm(href) {
    if (!window.flCanTrackMarketing || !utmOn || !utmStr || !isTrackableHref(href)) return href;
    if (href.includes('utm_')) return href;

    const parts = href.split('#');
    const base = parts[0];
    const hash = parts[1] ? `#${parts[1]}` : '';
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}${utmStr}${hash}`;
  }

  function trackClick(label, url) {
    try {
      if (window.flCanTrackAnalytics && typeof window.gtag === 'function') {
        window.gtag('event', 'linktree_click', { link_text: label, link_url: url });
      }
      if (window.flCanTrackAnalytics && typeof window.ga === 'function') {
        window.ga('send', 'event', 'Linktree', 'Click', label);
      }
      if (window.flCanTrackAnalytics && window.flCanTrackMarketing && window._learnq) {
        window._learnq.push([
          'track',
          'Linktree Click',
          {
            'Link Label': label,
            'Link URL': url,
            Source: 'linktree',
            Timestamp: new Date().toISOString()
          }
        ]);
      }
    } catch (_) {
      /* no-op */
    }
  }

  function storeLastLink(label, href) {
    if (!isTrackableHref(href)) return;
    try {
      window.flPreferenceStorage.setItem(
        LAST_LINK_KEY,
        JSON.stringify({ label, href, ts: Date.now() })
      );
    } catch (_) {
      /* no-op */
    }
  }

  function normalizeLabel(link) {
    return (link.dataset.ltLabel || link.textContent || 'Link').trim();
  }

  const trackedLinks = page.querySelectorAll('[data-lt-link]');
  trackedLinks.forEach((link) => {
    link.addEventListener('click', () => {
      const label = normalizeLabel(link);
      let currentHref = link.getAttribute('href') || '';

      if (isTrackableHref(currentHref)) {
        const updatedHref = appendUtm(currentHref);
        if (updatedHref !== currentHref) {
          if (!marketingHrefBeforeMutation.has(link)) {
            marketingHrefBeforeMutation.set(link, currentHref);
          }
          currentHref = updatedHref;
          link.setAttribute('href', updatedHref);
        }
      }

      trackClick(label, currentHref);
      storeLastLink(label, currentHref);
    });
  });

  /* Share button */
  async function shareCurrentPage(button) {
    const url = window.location.href;
    const title = document.title;

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(url);
        if (button) {
          const previous = button.textContent;
          button.textContent = 'Copied';
          setTimeout(() => {
            button.textContent = previous;
          }, 1400);
        }
      }
      trackClick('Share', url);
    } catch (_) {
      /* canceled */
    }
  }

  page.querySelectorAll('[data-lt-share]').forEach((button) => {
    button.addEventListener('click', () => {
      shareCurrentPage(button.querySelector('span') || button);
    });
  });

  /* Search + filters */
  const directoryRoot = page.querySelector('[data-lt-link-directory]');
  const directoryItems = directoryRoot ? Array.from(directoryRoot.querySelectorAll('.lt__directory-item')) : [];
  const searchInput = page.querySelector('[data-lt-search]');
  const filterButtons = Array.from(page.querySelectorAll('[data-lt-filter]'));
  const emptyState = page.querySelector('[data-lt-empty]');
  let activeFilter = 'all';

  function applyDirectoryFilter() {
    if (!directoryItems.length) return;
    const query = (searchInput?.value || '').trim().toLowerCase();
    let visible = 0;

    directoryItems.forEach((item) => {
      const category = (item.dataset.ltCategory || 'all').toLowerCase();
      const searchable = (item.dataset.ltSearchable || item.textContent || '').toLowerCase();
      const matchCategory = activeFilter === 'all' || category === activeFilter || category === 'all';
      const matchQuery = !query || searchable.includes(query);
      const shouldShow = matchCategory && matchQuery;

      item.hidden = !shouldShow;
      if (shouldShow) visible += 1;
    });

    if (emptyState) {
      emptyState.hidden = visible > 0;
    }
  }

  if (searchInput) {
    searchInput.addEventListener('input', applyDirectoryFilter);
  }

  filterButtons.forEach((button) => {
    button.addEventListener('click', () => {
      activeFilter = button.dataset.ltFilter || 'all';
      filterButtons.forEach((btn) => {
        const isActive = btn === button;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-pressed', String(isActive));
      });
      applyDirectoryFilter();
    });
  });

  applyDirectoryFilter();

  /* Smart recommendation */
  const recoTitle = page.querySelector('[data-lt-reco-title]');
  const recoReason = page.querySelector('[data-lt-reco-reason]');
  const recoLink = page.querySelector('[data-lt-reco-link]');
  const lastLinkButton = page.querySelector('[data-lt-last-link]');
  const lastLinkDefault = lastLinkButton ? {
    href: lastLinkButton.getAttribute('href') || '',
    label: lastLinkButton.dataset.ltLabel || '',
    text: lastLinkButton.textContent || ''
  } : null;

  // The configured featured link stays stable. We do not infer intent or
  // conversion behavior from local time, referrer, or campaign parameters.

  /* Continue last link */
  function resetLastLink() {
    if (!lastLinkButton || !lastLinkDefault) return;
    lastLinkButton.hidden = true;
    lastLinkButton.setAttribute('href', lastLinkDefault.href);
    lastLinkButton.dataset.ltLabel = lastLinkDefault.label;
    lastLinkButton.textContent = lastLinkDefault.text;
    marketingHrefBeforeMutation.delete(lastLinkButton);
  }

  function setupLastLink() {
    if (!lastLinkButton) return;

    try {
      const raw = window.flPreferenceStorage.getItem(LAST_LINK_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);
      if (!parsed || !parsed.href) return;

      lastLinkButton.hidden = false;
      lastLinkButton.href = parsed.href;
      lastLinkButton.dataset.ltLabel = `Continue - ${parsed.label || 'Last link'}`;
      if (parsed.label) {
        lastLinkButton.textContent = `Continue: ${parsed.label}`;
      }
    } catch (_) {
      /* no-op */
    }
  }

  function restoreMarketingHrefs() {
    marketingHrefBeforeMutation.forEach((href, link) => {
      if (link.isConnected) link.setAttribute('href', href);
    });
    marketingHrefBeforeMutation.clear();
  }

  function syncPrivacyDependentLinks() {
    if (window.flCanTrackMarketing !== true) restoreMarketingHrefs();
    if (window.flCanTrackPreferences !== true) {
      resetLastLink();
      return;
    }
    if (lastLinkButton && lastLinkButton.hidden) setupLastLink();
  }

  document.addEventListener('fl:privacy-consent-change', syncPrivacyDependentLinks);
  window.flRunWhenPrivacyReady(syncPrivacyDependentLinks);

  /* Album accordion animation */
  page.querySelectorAll('[data-lt-album]').forEach((details) => {
    const content = details.querySelector('.lt__album-platforms');
    if (!content) return;

    content.style.overflow = 'hidden';
    content.style.transition = 'max-height 0.28s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s ease';

    const setState = () => {
      if (details.open) {
        content.style.maxHeight = `${content.scrollHeight}px`;
        content.style.opacity = '1';
      } else {
        content.style.maxHeight = '0px';
        content.style.opacity = '0';
      }
    };

    setState();
    details.addEventListener('toggle', setState);
  });

  /* Dock active state */
  const dockLinks = Array.from(page.querySelectorAll('[data-lt-dock] .lt__dock-item'));
  if (dockLinks.length) {
    const currentPath = window.location.pathname.replace(/\/$/, '') || '/';

    dockLinks.forEach((link) => {
      let linkPath = '/';
      try {
        linkPath = new URL(link.href, window.location.origin).pathname.replace(/\/$/, '') || '/';
      } catch (_) {
        linkPath = '/';
      }

      const active = linkPath !== '/' && currentPath.startsWith(linkPath);
      link.classList.toggle('is-active', active);

      link.addEventListener('click', () => {
        dockLinks.forEach((node) => node.classList.remove('is-active'));
        link.classList.add('is-active');
      });
    });

    if (!dockLinks.some((node) => node.classList.contains('is-active'))) {
      dockLinks[0].classList.add('is-active');
    }
  }

  /* Newsletter submit state */
  const newsletterForm = page.querySelector('.lt__newsletter-form');
  if (newsletterForm) {
    const newsletterButton = newsletterForm.querySelector('.lt__newsletter-btn');
    const newsletterButtonIdleNodes = newsletterButton
      ? Array.from(newsletterButton.childNodes).map((node) => node.cloneNode(true))
      : [];

    const resetNewsletterButton = () => {
      const button = newsletterButton;
      if (!button) return;
      button.disabled = false;
      button.style.opacity = '';
      button.replaceChildren(...newsletterButtonIdleNodes.map((node) => node.cloneNode(true)));
      button.setAttribute('aria-label', 'Subscribe');
    };

    newsletterForm.addEventListener('submit', () => {
      const button = newsletterButton;
      if (!button) return;
      button.disabled = true;
      button.style.opacity = '0.75';
      button.textContent = 'Sending';
      button.setAttribute('aria-label', 'Sending');
      window.setTimeout(resetNewsletterButton, 12000);
    });

    window.addEventListener('pageshow', resetNewsletterButton);
  }
})();
